import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueries } from 'react-query';
import { Container } from '../../Components/Container';
import { Information } from 'Components/Information';
import { PageLoaderList } from 'Components/PageLoader/List';
import { NoData } from 'Components/NoData';
import { UsageBar } from 'Components/UsageBar';
import { ModalConfirmDelete } from 'Components/ModalConfirmDelete';
import {
  Button,
  Chip,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { deleteCompany, getPartnerDashboard } from 'services/company-service';
import { getPolicyMetrics } from 'services/policies-service';
import { getGaMetrics } from 'services/gap-analysis';
import { getUserMetrics } from 'services/users-service';
import { getCookieMetrics } from 'services/cookies-service';
import { useAuthUser, useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
import { hasModulePermission } from 'utils/permission_utils';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';


export const CompaniesPartner = () => {
  const navigate = useNavigate();
  const user = useSelector(useAuthUser);
  const selectedTenant = useSelector(useAuthTenant);
  const SwalReact = withReactContent(Swal);

  const [reFetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [page, setPage] = useState(1);

  const {
    isLoading,
    error,
    data: dashboard,
  } = useQuery(['getPartnerDashboard', reFetch, page, selectedTenant?._id], async () => {
    const result = await getPartnerDashboard(page, 15, selectedTenant?._id);
    return result?.data ?? { data: [], page, totalPages: 0, summary: null };
  });

  useEffect(() => {
    if (error && error.response?.status === 403) {
      navigate('/no-permission');
    }
  }, [error, navigate]);

  const tenants = dashboard?.data ?? [];

  const metricsQueries = useQueries(
    tenants.map(tenant => ({
      queryKey: ['partner-metrics', tenant.slug],
      queryFn: () =>
        Promise.all([
          getPolicyMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalPolicies: null })),
          getGaMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalFrameworks: null })),
          getUserMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalUsers: null })),
          getCookieMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalDisclaimers: null })),
        ]),
      staleTime: 5 * 60 * 1000,
    }))
  );

  const getMetrics = (idx) => {
    const q = metricsQueries[idx];
    if (!q?.data) return { policy: null, ga: null, users: null, cookies: null };
    const [policy, ga, users, cookies] = q.data;
    return { policy, ga, users, cookies };
  };

  const hideDelete = () => !hasModulePermission(user.permissions, ['super_admin_global', 'admin_global']);

  const onDelete = item => { setItemToDelete(item); setDeleteOpen(true); };
  const onDeleteClose = () => { setItemToDelete(null); setDeleteOpen(false); };

  const onDeleteConfirm = async () => {
    try {
      const result = await deleteCompany(itemToDelete?._id);
      if (result.status === 204) {
        onDeleteClose();
        setRefetch(!reFetch);
      } else {
        throw new Error('Ocorreu um erro ao tentar remover a Empresa!');
      }
    } catch (error) {
      SwalReact.fire({ title: 'Erro!', text: error.response?.data?.message ?? error.message, icon: 'error' });
    }
  };

  return (
    <Container
      title="Clientes"
      breadcrumb="Configurações Gerais / Clientes"
    >
      <Information
        title="O que são clientes"
        description="Tenants gerenciados pelo seu parceiro. Monitore o uso de cada cliente."
        style={{ marginTop: '24px' }}
      />

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2, mb: 1 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/companies-partner/add')}>
          Novo Cliente
        </Button>
      </Stack>

      {isLoading && <PageLoaderList />}
      {error && <Typography color="error">Erro ao carregar dados.</Typography>}

      {!isLoading && !error && tenants.length === 0 && (
        <NoData description="Não há Clientes para exibir" />
      )}

      {!isLoading && !error && tenants.length > 0 && (
        <>
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Plano</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Políticas</TableCell>
                <TableCell>Gap Analysis</TableCell>
                <TableCell>Usuários</TableCell>
                <TableCell>Cookies</TableCell>
                {!hideDelete() && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((tenant, idx) => {
                const { policy, ga, users, cookies } = getMetrics(idx);
                const limits = tenant.planLimits;
                return (
                  <TableRow key={tenant._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{tenant.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{tenant.slug}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={tenant.signedPlan ?? '—'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tenant.active ? 'Ativo' : 'Inativo'}
                        size="small"
                        color={tenant.active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <UsageBar current={policy?.totalPolicies ?? 0} limit={limits?.maxPoliciesLimit} />
                    </TableCell>
                    <TableCell>
                      <UsageBar current={ga?.totalFrameworks ?? 0} limit={limits?.maxGapAnalysisLimit} />
                    </TableCell>
                    <TableCell>
                      <UsageBar current={users?.totalUsers ?? 0} limit={limits?.maxUsersLimit} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {cookies?.totalDisclaimers ?? '—'}
                      </Typography>
                    </TableCell>
                    {!hideDelete() && (
                      <TableCell>
                        <Button size="small" color="error" onClick={() => onDelete(tenant)}>
                          Excluir
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <Stack sx={{ justifyContent: 'center', flexDirection: 'row', mt: 2 }}>
            <Pagination
              count={dashboard.totalPages}
              onChange={(_, p) => setPage(p)}
              color="primary"
              page={page}
              showFirstButton
              showLastButton
            />
          </Stack>
        </>
      )}

      <ModalConfirmDelete
        description={<>Tem certeza que deseja excluir <strong>{` ${itemToDelete?.name} `}</strong> como cliente?</>}
        open={deleteOpen}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />

    </Container>
  );
};
